import React from 'react';
import { motion } from 'framer-motion';
import { XMarkIcon, ChevronDownIcon, ArrowRightIcon } from '@heroicons/react/24/outline';
import { GitBranch } from 'lucide-react';
import { ListPageSkeleton } from '../ui/PageSkeleton';
import UserAvatar from '../ui/UserAvatar';
import { useLanguage } from '../../contexts/LanguageContext';

function ReferralPersonRow({ username, email, avatarUrl, variant = 'referral' }) {
  const isRoot = variant === 'root';

  return (
    <div
      className={`flex items-center gap-2 rounded-xl border px-3 py-2 text-left backdrop-blur-sm ${
        isRoot
          ? 'border-orange-300/70 bg-orange-50/75  '
          : 'border-white/50 bg-white/55  '
      }`}
    >
      <UserAvatar
        src={avatarUrl}
        alt={username}
        size="xs"
        variant={isRoot ? 'brand' : 'default'}
        className={isRoot ? 'shadow-soft' : ''}
      />
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-semibold text-secondary-900 ">
          {username}
        </p>
        {email && (
          <p className="truncate text-xs text-secondary-500 ">{email}</p>
        )}
      </div>
    </div>
  );
}

function ClientReferralTreeOverlay({ clientUsername, tree, loading, onClose }) {
  const { t, formatMessage } = useLanguage();
  const rt = t.enrola.referralTree;
  const root = tree?.root;
  const directReferrals = tree?.direct_referrals ?? [];
  const directCount = tree?.direct_referrals_count ?? directReferrals.length;
  const hasReferrals = directReferrals.length > 0;

  return (
    <motion.div
      role="dialog"
      aria-modal="true"
      aria-label={formatMessage(rt.dialogLabel, { name: clientUsername })}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      className="absolute inset-0 z-20 flex h-full max-h-full min-h-0 flex-col overflow-hidden rounded-[inherit]"
      onClick={(e) => e.stopPropagation()}
    >
      <div
        className="pointer-events-none absolute inset-0 rounded-[inherit] bg-white/35 backdrop-blur-2xl backdrop-saturate-150 "
        aria-hidden
      />
      <div
        className="pointer-events-none absolute inset-0 rounded-[inherit] ring-1 ring-inset ring-white/60 "
        aria-hidden
      />

      <div className="relative z-10 flex h-full min-h-0 flex-col p-4">
        <div className="mb-3 flex shrink-0 items-start justify-between gap-2">
          <div className="flex min-w-0 items-center gap-2">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-orange-500/20 text-orange-600 backdrop-blur-sm  ">
              <GitBranch className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <h4 className="text-sm font-bold text-secondary-900 ">
                {rt.title}
              </h4>
              <p className="truncate text-xs text-secondary-600 ">
                {clientUsername}
                {!loading && (
                  <span className="text-secondary-500">
                    {' '}
                    {formatMessage(rt.directCount, { count: directCount })}
                  </span>
                )}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 rounded-lg bg-white/40 p-1.5 text-secondary-600 backdrop-blur-sm transition-colors hover:bg-white/70 hover:text-secondary-900    "
            aria-label={rt.close}
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="relative min-h-0 flex-1 overflow-hidden">
          <div className="h-full min-h-0 overflow-y-auto overscroll-contain scroll-smooth pr-1">
            {loading ? (
              <ListPageSkeleton rows={3} showHeader={false} className="py-1" />
            ) : !hasReferrals ? (
              <div className="flex min-h-[120px] flex-col items-center justify-center rounded-xl border border-dashed border-secondary-300/60 bg-white/30 px-4 text-center backdrop-blur-sm  ">
                <GitBranch className="mb-2 h-8 w-8 text-secondary-400 " />
                <p className="text-sm font-medium text-secondary-700 ">
                  {rt.emptyTitle}
                </p>
                <p className="mt-1 text-xs text-secondary-500 ">
                  {rt.emptyDesc}
                </p>
              </div>
            ) : (
              <div className="space-y-2 pb-1">
                <ReferralPersonRow
                  username={root?.username || clientUsername}
                  email={root?.email}
                  avatarUrl={root?.avatar_url}
                  variant="root"
                />

                <div className="flex justify-center py-0.5" aria-hidden>
                  <ChevronDownIcon className="h-5 w-5 text-orange-500 " />
                </div>

                <p className="text-center text-[10px] font-semibold uppercase tracking-wide text-secondary-500 ">
                  {rt.sectionLabel}
                </p>

                <ul className="space-y-2">
                  {directReferrals.map((referral) => (
                    <li key={referral.id} className="list-none">
                      <div className="flex items-center gap-2">
                        <ArrowRightIcon
                          className="h-4 w-4 shrink-0 text-orange-500 "
                          aria-hidden
                        />
                        <div className="min-w-0 flex-1">
                          <ReferralPersonRow
                            username={referral.username}
                            email={referral.email}
                            avatarUrl={referral.avatar_url}
                          />
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
          {hasReferrals && !loading && (
            <div
              className="pointer-events-none absolute inset-x-0 bottom-0 h-8 bg-gradient-to-t from-white/50 to-transparent "
              aria-hidden
            />
          )}
        </div>
      </div>
    </motion.div>
  );
}

export default ClientReferralTreeOverlay;
